<script setup>
import { ref, computed, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  field: { type: Object, required: true },
  ticketAttributes: { type: Array, default: () => [] },
  captureSources: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:field', 'save', 'cancel']);

const localField = ref({ ...props.field });

watch(
  () => props.field,
  val => {
    localField.value = { ...val, examples: [...(val.examples || [])] };
  },
  { immediate: true }
);

const updateField = (key, value) => {
  localField.value = { ...localField.value, [key]: value };
  emit('update:field', localField.value);
};

const selectedAttribute = computed(() =>
  props.ticketAttributes.find(attr => attr.attributeKey === localField.value.key)
);

const attributeTypeLabel = computed(
  () => selectedAttribute.value?.attributeDisplayType || localField.value.type
);

const onAttributeChange = key => {
  const attr = props.ticketAttributes.find(item => item.attributeKey === key);
  if (!attr) {
    localField.value = {
      ...localField.value,
      key: '',
      label: '',
      type: '',
    };
    emit('update:field', localField.value);
    return;
  }

  localField.value = {
    ...localField.value,
    key: attr.attributeKey,
    label: attr.attributeDisplayName,
    type: attr.attributeDisplayType,
  };
  emit('update:field', localField.value);
};

const priorities = [
  { value: 'required', label: 'Obligatorio' },
  { value: 'recommended', label: 'Recomendado' },
  { value: 'optional', label: 'Opcional' },
];

// Examples management
const newExample = ref('');

const addExample = () => {
  if (newExample.value.trim()) {
    const examples = [
      ...(localField.value.examples || []),
      newExample.value.trim(),
    ];
    updateField('examples', examples);
    newExample.value = '';
  }
};

const removeExample = index => {
  const examples = localField.value.examples.filter((_, i) => i !== index);
  updateField('examples', examples);
};

const showSkipResponse = computed(() =>
  ['recommended', 'optional'].includes(localField.value.priority)
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
  <div class="flex flex-col w-full">
    <!-- Header with back button -->
    <div class="flex items-center gap-2 pb-3 mb-3 border-b border-n-weak">
      <Button
        icon="i-lucide-arrow-left"
        slate
        xs
        faded
        type="button"
        @click="$emit('cancel')"
      />
      <span class="text-sm font-medium text-n-slate-12">
        {{ field.key ? $t('WORKFLOW_CONFIG.EDIT_FIELD') : $t('WORKFLOW_CONFIG.ADD_FIELD') }}
      </span>
    </div>

    <form class="flex flex-col gap-4" @submit.prevent="$emit('save')">
      <!-- Ticket Custom Attribute -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('WORKFLOW_CONFIG.TICKET_CUSTOM_ATTRIBUTE') }} *
        </label>
        <select
          :value="localField.key"
          required
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
          @change="onAttributeChange($event.target.value)"
        >
          <option value="">
            {{ $t('WORKFLOW_CONFIG.SELECT_TICKET_ATTRIBUTE') }}
          </option>
          <option
            v-if="localField.key && !selectedAttribute"
            :value="localField.key"
          >
            {{ localField.label || localField.key }}
            ({{ $t('WORKFLOW_CONFIG.ATTRIBUTE_NOT_FOUND') }})
          </option>
          <option
            v-for="attr in ticketAttributes"
            :key="attr.attributeKey"
            :value="attr.attributeKey"
          >
            {{ attr.attributeDisplayName }} ({{ attr.attributeKey }})
          </option>
        </select>
        <span class="text-xs text-n-slate-10">
          {{ $t('WORKFLOW_CONFIG.TICKET_ATTRIBUTE_HELP') }}
        </span>
      </div>

      <div
        v-if="localField.key"
        class="grid grid-cols-2 gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-3 text-xs"
      >
        <div class="flex flex-col gap-1">
          <span class="font-medium text-n-slate-10">
            {{ $t('WORKFLOW_CONFIG.FIELD_KEY') }}
          </span>
          <span class="font-mono text-n-slate-12">{{ localField.key }}</span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="font-medium text-n-slate-10">
            {{ $t('WORKFLOW_CONFIG.FIELD_TYPE') }}
          </span>
          <span class="text-n-slate-12">{{ attributeTypeLabel }}</span>
        </div>
      </div>

      <!-- Priority -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('WORKFLOW_CONFIG.FIELD_PRIORITY') }}
          </label>
          <select
            :value="localField.priority"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
            @change="updateField('priority', $event.target.value)"
          >
            <option v-for="p in priorities" :key="p.value" :value="p.value">
              {{ p.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Order & Capture From -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('WORKFLOW_CONFIG.FIELD_ORDER') }}
          </label>
          <input
            :value="localField.order"
            type="number"
            min="1"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
            @input="updateField('order', parseInt($event.target.value) || 1)"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('WORKFLOW_CONFIG.CAPTURE_FROM') }}
          </label>
          <select
            :value="localField.capture_from || ''"
            class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
            @change="updateField('capture_from', $event.target.value || null)"
          >
            <option value="">{{ $t('WORKFLOW_CONFIG.NO_AUTO_CAPTURE') }}</option>
            <optgroup
              v-if="captureSources.filter(s => s.group === 'contact').length"
              :label="$t('WORKFLOW_CONFIG.CONTACT_SOURCES')"
            >
              <option
                v-for="src in captureSources.filter(s => s.group === 'contact')"
                :key="src.key"
                :value="src.key"
              >
                {{ src.label }}
              </option>
            </optgroup>
            <optgroup
              v-if="captureSources.filter(s => s.group === 'contract').length"
              :label="$t('WORKFLOW_CONFIG.CONTRACT_SOURCES')"
            >
              <option
                v-for="src in captureSources.filter(s => s.group === 'contract')"
                :key="src.key"
                :value="src.key"
              >
                {{ src.label }}
              </option>
            </optgroup>
            <optgroup
              v-if="captureSources.filter(s => s.group === 'contact_custom').length"
              :label="$t('WORKFLOW_CONFIG.CONTACT_CUSTOM_SOURCES')"
            >
              <option
                v-for="src in captureSources.filter(s => s.group === 'contact_custom')"
                :key="src.key"
                :value="src.key"
              >
                {{ src.label }}
              </option>
            </optgroup>
            <optgroup
              v-if="captureSources.filter(s => s.group === 'conversation_custom').length"
              :label="$t('WORKFLOW_CONFIG.CONVERSATION_CUSTOM_SOURCES')"
            >
              <option
                v-for="src in captureSources.filter(s => s.group === 'conversation_custom')"
                :key="src.key"
                :value="src.key"
              >
                {{ src.label }}
              </option>
            </optgroup>
          </select>
          <span class="text-xs text-n-slate-10">
            {{ $t('WORKFLOW_CONFIG.CAPTURE_FROM_HELP') }}
          </span>
        </div>
      </div>

      <!-- Prompt -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('WORKFLOW_CONFIG.FIELD_PROMPT') }}
        </label>
        <textarea
          :value="localField.prompt"
          rows="2"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none text-sm"
          :placeholder="$t('WORKFLOW_CONFIG.FIELD_PROMPT_PLACEHOLDER')"
          @input="updateField('prompt', $event.target.value)"
        />
      </div>

      <!-- Examples -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('WORKFLOW_CONFIG.FIELD_EXAMPLES') }}
        </label>
        <div class="flex gap-2">
          <input
            v-model="newExample"
            type="text"
            class="flex-1 px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none text-sm"
            placeholder="Agregar ejemplo..."
            @keyup.enter="addExample"
          />
          <Button
            icon="i-lucide-plus"
            slate
            xs
            faded
            type="button"
            @click="addExample"
          />
        </div>
        <div v-if="localField.examples?.length" class="flex flex-wrap gap-1 mt-1">
          <span
            v-for="(example, i) in localField.examples"
            :key="i"
            class="flex items-center gap-1 px-2 py-1 text-xs bg-n-solid-3 rounded"
          >
            {{ example }}
            <button type="button" class="text-n-slate-10 hover:text-n-slate-12" @click="removeExample(i)">
              &times;
            </button>
          </span>
        </div>
      </div>

      <!-- Skip Response (only for recommended/optional) -->
      <div v-if="showSkipResponse" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('WORKFLOW_CONFIG.FIELD_SKIP_RESPONSE') }}
        </label>
        <input
          :value="localField.skip_response"
          type="text"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none text-sm"
          :placeholder="$t('WORKFLOW_CONFIG.FIELD_SKIP_RESPONSE_PLACEHOLDER')"
          @input="updateField('skip_response', $event.target.value)"
        />
        <span class="text-xs text-n-slate-10">{{ $t('WORKFLOW_CONFIG.FIELD_SKIP_RESPONSE_HELP') }}</span>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2 pt-4 border-t border-n-weak mt-2">
        <Button slate faded :label="$t('WORKFLOW_CONFIG.CANCEL')" type="button" @click="$emit('cancel')" />
        <Button
          type="submit"
          :disabled="!selectedAttribute"
          :label="$t('WORKFLOW_CONFIG.SAVE_FIELD')"
        />
      </div>
    </form>
  </div>
</template>
